'use client';

import { useMemo } from 'react';

import { SmallLotPricingPackaging, SmallLotPricingTier } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { getActiveTierIndex, getLastTierQuantityTo, sortTiers } from '../../../../../utils/quote-pricing/small-lot-tier.utils';

interface Props {
   tiers: SmallLotPricingTier[];
   packaging: SmallLotPricingPackaging;
   selectedQuantity: number;
   isQuantityValid: boolean;
   hasMtsLocal?: boolean;
   onRequestQuote: () => void;
}

export const SmallLotQuantityBreaks = ({ tiers, packaging, selectedQuantity, isQuantityValid, hasMtsLocal, onRequestQuote }: Props) => {
   const translate = useTranslations();
   const formatter = useFormatter();

   const sortedTiers = useMemo(() => sortTiers(tiers), [tiers]);
   const lastTierQuantityTo = getLastTierQuantityTo(sortedTiers);

   const activeIndex = useMemo(
      () => (isQuantityValid ? getActiveTierIndex(selectedQuantity, sortedTiers, lastTierQuantityTo) : -1),
      [isQuantityValid, sortedTiers, selectedQuantity, lastTierQuantityTo]
   );

   return (
      <Box direction="column" gap="sm">
         <Table rowDividers>
            <TableBody>
               {sortedTiers.map((tier, index) => {
                  const isLast = index === sortedTiers.length - 1;
                  const toLabel = `${tier.quantityTo.amount}${isLast ? '+' : ''}`;
                  return (
                     <TableRow key={tier.quantityFrom.amount} active={activeIndex === index}>
                        <TableCell>
                           <Typography variant="small">
                              {index === 0 || tier.quantityFrom.amount === tier.quantityTo.amount
                                 ? `${toLabel} ${tier.quantityTo.unitOfMeasure}`
                                 : `${tier.quantityFrom.amount}–${toLabel} ${tier.quantityTo.unitOfMeasure}`}
                           </Typography>
                        </TableCell>
                        <TableCell>
                           <Box direction="row" gap="2xs" items="baseline">
                              <Typography variant="small">{packaging.name}</Typography>
                           </Box>
                        </TableCell>
                        <TableCell align="right">
                           <Box direction="column" items="end" gap="3xs">
                              <Box direction="row" items="baseline" justify="end" gap="xs">
                                 <Typography variant="caption" color="error" lineThrough>
                                    {formatter.unitPrice({ price: tier.listPrice, unitOfMeasure: packaging.unitOfMeasure })}
                                 </Typography>
                                 <Typography variant="small-highlight">
                                    {formatter.unitPrice({ price: tier.discountedPrice, unitOfMeasure: packaging.unitOfMeasure })}
                                 </Typography>
                              </Box>
                              {hasMtsLocal && (
                                 <Box direction="row" items="center" justify="end" gap="2xs">
                                    <Icon icon="truck" size="small" color="subtle" />
                                    <Typography variant="small" color="subtle">
                                       {translate('request-for-quotation.lead-time.mts-local-days')}
                                    </Typography>
                                 </Box>
                              )}
                           </Box>
                        </TableCell>
                     </TableRow>
                  );
               })}
            </TableBody>
         </Table>

         <Box justify="center" items="center" gap="xs">
            <Box gap="2xs" items="center">
               <Icon icon="info-circle" color="subtle" size="small" />
               <Typography component="span" variant="small" color="subtle">
                  {translate('dynamic-product-block.need-different-packaging-or-quantity')}
               </Typography>
               <Typography component="span" variant="small" color="primary" underline>
                  <Link onClick={onRequestQuote}>{translate('dynamic-product-block.small-lot.request-quote')}</Link>
               </Typography>
            </Box>
         </Box>
      </Box>
   );
};
